// src/pages/pmc/PmcApprovalDeskPane.js

import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  addDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast from "react-hot-toast";

const cx = (...xs) => xs.filter(Boolean).join(" ");

/* ---------------- Helpers ---------------- */

function clean(v = "") {
  return String(v || "").trim().toLowerCase();
}

function isStrictNafedApply(row = {}) {
  return (
    row.nafedProject === true &&
    row.projectSource === "NAFED" &&
    row.applicationPortal === "NAFED_PACS_APPLY"
  );
}

// Audit-archived applications keep linked workflow/MOU/tender data safe, but
// must never remain visible in active PMC operational screens or statistics.
function isActiveProject(row = {}) {
  const archiveState = String(
    row.archiveStatus || row.recordStatus || row.deletionStatus || ""
  ).toUpperCase();

  return (
    row.isDeleted !== true &&
    row.deleted !== true &&
    row.archived !== true &&
    row.isArchived !== true &&
    ![
      "DELETED",
      "ARCHIVED",
      "DELETED_FROM_PMC_ACTIVE_LIST",
      "REMOVED_FROM_ACTIVE_LIST",
    ].includes(archiveState)
  );
}

function applyNafedScopeFilter(list = [], nafedScope = {}) {
  const scopeState = clean(nafedScope?.state);

  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  const allowedProjectIds = (
    nafedScope?.allowedProjectIds ||
    nafedScope?.assignedProjectIds ||
    []
  ).map(String);

  return list.filter((row) => {
    const stateOk =
      !scopeState || clean(row.state) === scopeState;

    const districtOk =
      !allowedDistricts.length ||
      allowedDistricts.includes(clean(row.district));

    const projectOk =
      !allowedProjectIds.length ||
      allowedProjectIds.includes(String(row.id));

    return (
      isStrictNafedApply(row) &&
      isActiveProject(row) &&
      stateOk &&
      districtOk &&
      projectOk
    );
  });
}

function normalizeDistrictName(v = "") {
  const raw = String(v || "").trim();
  const compact = raw.replace(/[\s_-]+/g, "").toLowerCase();

  if (compact === "vavtharad") return "VavTharad";
  if (compact === "banaskantha") return "Banaskantha";
  if (compact === "mahesana" || compact === "mehsana") {
    return "Mahesana";
  }
  if (compact === "patan") return "Patan";
  if (compact === "gandhinagar") return "Gandhinagar";
  if (compact === "ahmedabad") return "Ahmedabad";
  if (compact === "sabarkantha") return "Sabarkantha";
  if (compact === "aravalli") return "Aravalli";
  if (compact === "kutch") return "Kutch";
  if (compact === "surat") return "Surat";
  if (compact === "vadodara") return "Vadodara";
  if (compact === "bhavnagar") return "Bhavnagar";
  if (compact === "rajkot") return "Rajkot";

  return raw;
}

function normalizeRow(row = {}) {
  return {
    ...row,

    state: String(
      row.state ||
        row.stateName ||
        row.projectState ||
        row.pacsState ||
        ""
    ).trim(),

    district: normalizeDistrictName(
      row.district ||
        row.districtName ||
        row.projectDistrict ||
        row.pacsDistrict ||
        ""
    ),

    taluka: String(
      row.taluka ||
        row.block ||
        row.pacsTaluka ||
        ""
    ).trim(),

    village: String(
      row.village ||
        row.location ||
        row.pacsVillage ||
        ""
    ).trim(),
  };
}

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-sky-100 text-sky-700",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl bg-white shadow border border-emerald-100 px-4 py-3">
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">
        {label}
      </div>

      <div className="mt-1 flex items-end justify-between">
        <div className="text-xl font-extrabold text-emerald-800">
          {value}
        </div>

        {note && (
          <div className="text-[10px] text-slate-400 text-right max-w-[150px]">
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PmcApprovalDeskPane({ nafedScope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [districtFilter, setDistrictFilter] = useState("all");
  const [decisionFilter, setDecisionFilter] = useState("PENDING");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      setLoading(true);

      const snap = await getDocs(
        collection(db, "godown_projects")
      );

      const list = snap.docs
        .map((d) =>
          normalizeRow({
            id: d.id,
            ...d.data(),
          })
        )
        .sort(
          (a, b) =>
            (b.createdAt?.seconds || 0) -
            (a.createdAt?.seconds || 0)
        );

      const scopedNafedRows = applyNafedScopeFilter(
        list,
        nafedScope
      );

      setRows(scopedNafedRows);
    } catch (e) {
      console.error(
        "NAFED PMC approval loading failed:",
        e
      );

      toast.error(
        "Failed to load NAFED projects for approval."
      );

      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(nafedScope || {})]);

  const allDistricts = useMemo(() => {
    const set = new Set();

    rows.forEach((r) => {
      if (r.district) {
        set.add(r.district);
      }
    });

    return [
      "all",
      ...Array.from(set).sort(),
    ];
  }, [rows]);

  const filtered = useMemo(() => {
    let list = [...rows];

    if (decisionFilter !== "all") {
      list = list.filter(
        (r) =>
          (r.pmcDecision || "PENDING") ===
          decisionFilter
      );
    }

    if (districtFilter !== "all") {
      list = list.filter(
        (r) =>
          (r.district || "") ===
          districtFilter
      );
    }

    const needle = q.trim().toLowerCase();

    if (needle) {
      list = list.filter((r) => {
        const hay = [
          r.pacsName,
          r.pacsRegistrationNo,
          r.village,
          r.taluka,
          r.district,
          r.state,
          r.id,
          r.applicationId,
          r.contactName,
          r.contactPhone,
        ]
          .map((x) =>
            String(x || "").toLowerCase()
          )
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [
    rows,
    decisionFilter,
    districtFilter,
    q,
  ]);

  const stats = useMemo(() => {
    const total = rows.length;

    const pending = rows.filter(
      (r) =>
        (r.pmcDecision || "PENDING") ===
        "PENDING"
    ).length;

    const approved = rows.filter(
      (r) =>
        r.pmcDecision ===
        "APPROVED_TO_WORKFLOW"
    ).length;

    const rejected = rows.filter(
      (r) =>
        r.pmcDecision ===
        "REJECTED"
    ).length;

    return {
      total,
      pending,
      approved,
      rejected,
    };
  }, [rows]);

  const approveToWorkflow = async (proj) => {
    if (proj.siteVisitStatus !== "DONE") {
      const cont = window.confirm(
        "Site visit DONE nahi hai. Kya phir bhi workflow me approve karna hai?"
      );

      if (!cont) return;
    }

    const remark =
      window.prompt(
        "PMC approval remark (optional):",
        proj.pmcDecisionRemark || ""
      ) || "";

    try {
      let wfProjectId =
        proj.workflowProjectId;

      /*
       * Existing workflow ID saved hai,
       * lekin document delete/missing ho to
       * safely new workflow create hoga.
       */
      if (wfProjectId) {
        try {
          const wfSnap = await getDoc(
            doc(
              db,
              "wf_projects",
              wfProjectId
            )
          );

          if (!wfSnap.exists()) {
            wfProjectId = null;
          }
        } catch (e) {
          console.warn(
            "Existing workflow lookup failed:",
            e
          );

          wfProjectId = null;
        }
      }

      if (!wfProjectId) {
        const wfRef = await addDoc(
          collection(db, "wf_projects"),
          {
            title: `${
              proj.pacsName || "PACS"
            } – ${
              proj.capacityMt || ""
            } MT Godown`,

            pacsName:
              proj.pacsName || "",

            pacsContactName:
              proj.contactName || "",

            pacsContactPhone:
              proj.contactPhone || "",

            locationVillage:
              proj.village || "",

            taluka:
              proj.taluka || "",

            district:
              normalizeDistrictName(
                proj.district || ""
              ),

            state:
              proj.state ||
              nafedScope?.state ||
              "",

            /* Workflow starter */

            stage: "MOU",
            progress: 0,

            /* Project linking */

            godownProjectId:
              proj.id,

            bankProjectId:
              proj.id,

            applicationId:
              proj.applicationId || "",

            sourceProjectSource:
              proj.source || "PACS",

            sourceGodownProjectId:
              proj.id,

            sourceCollection:
              "godown_projects",

            /*
             * Strict NAFED identity.
             * Ye workflow ko Shine Agri
             * self project se separate rakhega.
             */

            nafedProject: true,
            projectSource: "NAFED",
            applicationPortal:
              "NAFED_PACS_APPLY",

            allocatedPmcEmail:
              nafedScope?.pmcEmail || "",

            allocatedPmcUid:
              nafedScope?.pmcUid || "",

            allocatedState:
              nafedScope?.state ||
              proj.state ||
              "",

            allocatedDistrict:
              normalizeDistrictName(
                proj.district || ""
              ),

            /* Project metadata */

            capacityMt:
              proj.capacityMt || null,

            godownType:
              proj.godownType || "",

            siteVisitStatus:
              proj.siteVisitStatus || "NEW",

            pmcDecision:
              "APPROVED_TO_WORKFLOW",

            pmcDecisionRemark:
              remark || "",

            /* Tender defaults */

            linkedTenderId: null,
            tenderId: null,
            tenderStage:
              "NOT_STARTED",

            tenderTitle: "",
            tenderMode: null,
            tenderAmount: null,
            tenderCity: "",

            awardedVendorUid: null,
            awardedVendorName: "",
            awardedAmount: null,
            awardedRank: "",
            awardedApplicationId: null,
            awardedAt: null,

            createdAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),
          }
        );

        wfProjectId = wfRef.id;
      } else {
        /*
         * Workflow already exists,
         * to project details sync karo.
         */

        await updateDoc(
          doc(
            db,
            "wf_projects",
            wfProjectId
          ),
          {
            title: `${
              proj.pacsName || "PACS"
            } – ${
              proj.capacityMt || ""
            } MT Godown`,

            pacsName:
              proj.pacsName || "",

            pacsContactName:
              proj.contactName || "",

            pacsContactPhone:
              proj.contactPhone || "",

            locationVillage:
              proj.village || "",

            taluka:
              proj.taluka || "",

            district:
              normalizeDistrictName(
                proj.district || ""
              ),

            state:
              proj.state ||
              nafedScope?.state ||
              "",

            godownProjectId:
              proj.id,

            bankProjectId:
              proj.id,

            applicationId:
              proj.applicationId || "",

            sourceProjectSource:
              proj.source || "PACS",

            sourceGodownProjectId:
              proj.id,

            sourceCollection:
              "godown_projects",

            nafedProject: true,
            projectSource: "NAFED",
            applicationPortal:
              "NAFED_PACS_APPLY",

            allocatedPmcEmail:
              nafedScope?.pmcEmail || "",

            allocatedPmcUid:
              nafedScope?.pmcUid || "",

            allocatedState:
              nafedScope?.state ||
              proj.state ||
              "",

            allocatedDistrict:
              normalizeDistrictName(
                proj.district || ""
              ),

            capacityMt:
              proj.capacityMt || null,

            godownType:
              proj.godownType || "",

            siteVisitStatus:
              proj.siteVisitStatus || "NEW",

            pmcDecision:
              "APPROVED_TO_WORKFLOW",

            pmcDecisionRemark:
              remark || "",

            stage: "MOU",
            progress: 0,

            updatedAt:
              serverTimestamp(),
          }
        );
      }

      /*
       * Main godown project update.
       */

      await updateDoc(
        doc(
          db,
          "godown_projects",
          proj.id
        ),
        {
          nafedProject: true,
          projectSource: "NAFED",
          applicationPortal:
            "NAFED_PACS_APPLY",

          allocatedPmcEmail:
            nafedScope?.pmcEmail || "",

          allocatedPmcUid:
            nafedScope?.pmcUid || "",

          allocatedState:
            nafedScope?.state ||
            proj.state ||
            "",

          allocatedDistrict:
            normalizeDistrictName(
              proj.district || ""
            ),

          pmcDecision:
            "APPROVED_TO_WORKFLOW",

          pmcDecisionRemark:
            remark,

          pmcDecisionAt:
            serverTimestamp(),

          workflowProjectId:
            wfProjectId,

          workflowStage:
            "MOU",

          workflowProgress:
            0,

          workflowUpdatedAt:
            serverTimestamp(),

          linkedTenderId:
            proj.linkedTenderId ||
            null,

          tenderStage:
            proj.tenderStage ||
            "NOT_STARTED",

          updatedAt:
            serverTimestamp(),
        }
      );

      toast.success(
        "Project approved and moved to workflow."
      );

      setRows((prev) =>
        prev.map((r) =>
          r.id === proj.id
            ? normalizeRow({
                ...r,

                pmcDecision:
                  "APPROVED_TO_WORKFLOW",

                pmcDecisionRemark:
                  remark,

                workflowProjectId:
                  wfProjectId,

                workflowStage:
                  "MOU",

                workflowProgress:
                  0,
              })
            : r
        )
      );
    } catch (e) {
      console.error(
        "PMC NAFED approval failed:",
        e
      );

      toast.error("Approval failed.");
    }
  };

  const rejectProject = async (proj) => {
    const reason =
      window.prompt(
        "PMC rejection reason:",
        proj.pmcDecisionRemark || ""
      ) || "";

    if (!reason) return;

    try {
      await updateDoc(
        doc(
          db,
          "godown_projects",
          proj.id
        ),
        {
          nafedProject: true,
          projectSource: "NAFED",
          applicationPortal:
            "NAFED_PACS_APPLY",

          allocatedPmcEmail:
            nafedScope?.pmcEmail || "",

          allocatedPmcUid:
            nafedScope?.pmcUid || "",

          allocatedState:
            nafedScope?.state ||
            proj.state ||
            "",

          allocatedDistrict:
            normalizeDistrictName(
              proj.district || ""
            ),

          pmcDecision:
            "REJECTED",

          pmcDecisionRemark:
            reason,

          pmcDecisionAt:
            serverTimestamp(),

          workflowUpdatedAt:
            serverTimestamp(),

          updatedAt:
            serverTimestamp(),
        }
      );

      if (proj.workflowProjectId) {
        try {
          await updateDoc(
            doc(
              db,
              "wf_projects",
              proj.workflowProjectId
            ),
            {
              nafedProject: true,
              projectSource: "NAFED",
              applicationPortal:
                "NAFED_PACS_APPLY",

              allocatedPmcEmail:
                nafedScope?.pmcEmail || "",

              allocatedPmcUid:
                nafedScope?.pmcUid || "",

              allocatedState:
                nafedScope?.state ||
                proj.state ||
                "",

              allocatedDistrict:
                normalizeDistrictName(
                  proj.district || ""
                ),

              pmcDecision:
                "REJECTED",

              pmcDecisionRemark:
                reason,

              updatedAt:
                serverTimestamp(),
            }
          );
        } catch (wfErr) {
          console.warn(
            "Workflow rejection sync failed:",
            wfErr
          );
        }
      }

      toast.success(
        "Project rejected."
      );

      setRows((prev) =>
        prev.map((r) =>
          r.id === proj.id
            ? normalizeRow({
                ...r,

                pmcDecision:
                  "REJECTED",

                pmcDecisionRemark:
                  reason,
              })
            : r
        )
      );
    } catch (e) {
      console.error(
        "NAFED project rejection failed:",
        e
      );

      toast.error(
        "Rejection failed."
      );
    }
  };

  return (
    <div className="space-y-4 approval-desk-page">
      <style>{`
        .approval-desk-page { width: 100%; min-width: 0; }
        .approval-table-wrap { width: 100%; overflow: visible; }
        .approval-table { width: 100%; table-layout: fixed; border-collapse: collapse; }
        .approval-table th,
        .approval-table td {
          overflow-wrap: anywhere;
          word-break: break-word;
          line-height: 1.45;
        }
        .approval-table th { padding: 10px 8px; font-size: 9px; }
        .approval-table td { padding: 10px 8px; font-size: 10px; }
        .approval-remark {
          max-height: 66px;
          overflow-y: auto;
          overflow-x: hidden;
          white-space: pre-wrap;
        }
        .approval-action-stack button {
          width: 100%;
          padding: 8px 6px;
          font-size: 9px;
          line-height: 1.3;
        }
        @media (max-width: 760px) {
          .approval-table-shell { overflow: visible; border: 0; background: transparent; box-shadow: none; }
          .approval-table-wrap { overflow: visible; }
          .approval-table,
          .approval-table tbody,
          .approval-table tr,
          .approval-table td { display: block; width: 100%; }
          .approval-table colgroup,
          .approval-table thead { display: none; }
          .approval-table tbody tr {
            margin-bottom: 12px;
            border: 1px solid #b8c4d1;
            border-radius: 12px;
            overflow: hidden;
            background: white;
          }
          .approval-table td {
            position: relative;
            min-height: 40px;
            padding: 10px 10px 10px 116px !important;
            border: 0 !important;
            border-bottom: 1px solid #e2e8f0 !important;
            background: white;
          }
          .approval-table td::before {
            content: attr(data-label);
            position: absolute;
            left: 10px;
            top: 11px;
            width: 96px;
            color: #526171;
            font-size: 9px;
            font-weight: 900;
            text-transform: uppercase;
          }
          .approval-table td:last-child { border-bottom: 0 !important; }
        }
      `}</style>
      {/* Statistics */}

      <div className="grid md:grid-cols-4 gap-3">
        <StatCard
          label="Assigned NAFED Projects"
          value={stats.total}
          note="Only NAFED Apply Form projects from PMC allocated districts."
        />

        <StatCard
          label="Pending PMC Approval"
          value={stats.pending}
        />

        <StatCard
          label="Approved to Workflow"
          value={stats.approved}
        />

        <StatCard
          label="Rejected"
          value={stats.rejected}
        />
      </div>

      {/* Filters */}

      <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 grid md:grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-[11px] font-semibold text-slate-600">
            PMC Decision
          </div>

          <select
            value={decisionFilter}
            onChange={(e) =>
              setDecisionFilter(
                e.target.value
              )
            }
            className="mt-1 w-full px-3 py-1.5 border rounded-lg bg-white text-xs"
          >
            <option value="PENDING">
              Pending
            </option>

            <option value="APPROVED_TO_WORKFLOW">
              Approved
            </option>

            <option value="REJECTED">
              Rejected
            </option>

            <option value="all">
              All
            </option>
          </select>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-slate-600">
            District
          </div>

          <select
            value={districtFilter}
            onChange={(e) =>
              setDistrictFilter(
                e.target.value
              )
            }
            className="mt-1 w-full px-3 py-1.5 border rounded-lg bg-white text-xs"
          >
            {allDistricts.map((d) => (
              <option
                key={d}
                value={d}
              >
                {d === "all"
                  ? "All Districts"
                  : d}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-slate-600">
            Search
          </div>

          <input
            value={q}
            onChange={(e) =>
              setQ(e.target.value)
            }
            placeholder="PACS / Village / Application ID / Mobile..."
            className="mt-1 w-full px-3 py-1.5 border rounded-lg bg-white text-xs"
          />
        </div>
      </div>

      {/* Refresh and Scope Information */}

      <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-600 font-semibold">
          Showing{" "}
          <b className="text-slate-900">
            {filtered.length}
          </b>{" "}
          NAFED records from the logged-in PMC's allocated districts only.
        </div>

        <button
          type="button"
          onClick={load}
          className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800"
        >
          Refresh Approval Desk
        </button>
      </div>

      {/* Approval Table */}

      <div className="approval-table-shell rounded-2xl bg-white/90 border border-slate-400 shadow-[0_12px_32px_rgba(15,23,42,0.16)] overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-sm font-semibold text-slate-500 text-center">
            Loading NAFED approval projects...
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-10 text-sm font-semibold text-slate-500 text-center">
            No NAFED projects found for this PMC allocation and selected filters.
          </div>
        ) : (
          <div className="approval-table-wrap">
            <table className="approval-table text-xs border border-slate-400 border-collapse">
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "23%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr className="bg-slate-900 text-[11px] text-slate-100">
                  <th className="border border-slate-400 text-left font-semibold">
                    Sr.
                  </th>

                  <th className="border border-slate-400 text-left font-semibold">
                    Application / PACS
                  </th>

                  <th className="border border-slate-400 text-left font-semibold">
                    State / Location
                  </th>

                  <th className="border border-slate-400 text-left font-semibold">
                    Visit Status
                  </th>

                  <th className="border border-slate-400 text-left font-semibold">
                    PMC Decision
                  </th>

                  <th className="border border-slate-400 text-left font-semibold">
                    Workflow Details
                  </th>

                  <th className="border border-slate-400 text-left font-semibold">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((r, idx) => {
                  const visitStatus =
                    r.siteVisitStatus || "NEW";

                  const visitColor =
                    visitStatus === "DONE"
                      ? "green"
                      : visitStatus === "PLANNED"
                      ? "blue"
                      : "yellow";

                  const pmcDec =
                    r.pmcDecision || "PENDING";

                  const pmcColor =
                    pmcDec ===
                    "APPROVED_TO_WORKFLOW"
                      ? "green"
                      : pmcDec === "REJECTED"
                      ? "red"
                      : "yellow";

                  return (
                    <tr
                      key={r.id}
                      className={cx(
                        "hover:bg-slate-50/80 transition-colors",
                        idx % 2 === 0
                          ? "bg-white"
                          : "bg-slate-50/40"
                      )}
                    >
                      <td data-label="Sr" className="border border-slate-400 align-top font-bold">
                        {idx + 1}
                      </td>

                      <td data-label="Application" className="border border-slate-400 align-top">
                        <div className="font-bold text-slate-900 text-xs">
                          {r.applicationId ||
                            r.id}
                        </div>

                        <div className="mt-1 font-semibold text-slate-800">
                          {r.pacsName || "—"}
                        </div>

                        <div className="text-[11px] text-slate-600">
                          Registration:{" "}
                          {r.pacsRegistrationNo ||
                            "—"}
                        </div>

                        <div className="text-[11px] text-slate-600">
                          Contact:{" "}
                          {r.contactName || "—"} |{" "}
                          {r.contactPhone || "—"}
                        </div>

                        <div className="mt-1 text-[11px] text-slate-700">
                          Capacity:{" "}
                          <b>
                            {r.capacityMt || "—"} MT
                          </b>
                          {r.godownType
                            ? ` (${r.godownType})`
                            : ""}
                        </div>

                        <div className="text-[10px] text-slate-400 mt-1 font-mono break-all">
                          Document ID: {r.id}
                        </div>
                      </td>

                      <td data-label="Location" className="border border-slate-400 align-top text-[11px] text-slate-800">
                        <div className="font-bold text-slate-900">
                          {r.state || "—"}
                        </div>

                        <div className="mt-1 font-semibold">
                          {r.district || "—"}
                        </div>

                        <div className="text-slate-500">
                          Taluka:{" "}
                          {r.taluka || "—"}
                        </div>

                        <div className="text-slate-500">
                          Village:{" "}
                          {r.village || "—"}
                        </div>
                      </td>

                      <td data-label="Site Visit" className="border border-slate-400 align-top">
                        <Badge color={visitColor}>
                          {visitStatus}
                        </Badge>

                        {r.siteVisitAssignedTo && (
                          <div className="mt-2 text-[10px] text-slate-500">
                            Assigned:{" "}
                            {r.siteVisitAssignedTo}
                          </div>
                        )}

                        {r.siteVisitPlanDate && (
                          <div className="text-[10px] text-slate-500">
                            Date:{" "}
                            {r.siteVisitPlanDate}
                          </div>
                        )}
                      </td>

                      <td data-label="PMC Decision" className="border border-slate-400 align-top text-[11px] text-slate-700">
                        <div className="mb-2">
                          <Badge color={pmcColor}>
                            {pmcDec}
                          </Badge>
                        </div>

                        {r.pmcDecisionRemark ? (
                          <div className="approval-remark text-[10px] text-slate-500 leading-relaxed">
                            {r.pmcDecisionRemark}
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-400">
                            No decision remark.
                          </div>
                        )}
                      </td>

                      <td data-label="Workflow" className="border border-slate-400 align-top text-[11px] text-slate-700">
                        {r.workflowProjectId ? (
                          <>
                            <div className="font-semibold text-slate-900">
                              Workflow Project Created
                            </div>

                            <div className="mt-1">
                              ID:
                              <span className="font-mono text-[10px] break-all">
                                {" "}
                                {r.workflowProjectId}
                              </span>
                            </div>

                            <div className="text-[10px] text-slate-500 mt-1">
                              Stage:{" "}
                              <b>
                                {r.workflowStage ||
                                  "MOU"}
                              </b>
                            </div>

                            <div className="text-[10px] text-slate-500">
                              Progress:{" "}
                              <b>
                                {r.workflowProgress ||
                                  0}
                                %
                              </b>
                            </div>
                          </>
                        ) : pmcDec ===
                          "APPROVED_TO_WORKFLOW" ? (
                          <span className="text-amber-700 text-[11px] font-semibold">
                            Approved, but workflow document is missing. Approve again to recreate it safely.
                          </span>
                        ) : (
                          <span className="text-slate-400 text-[11px]">
                            Project has not yet been moved to workflow.
                          </span>
                        )}
                      </td>

                      <td data-label="Actions" className="border border-slate-400 align-top">
                        <div className="approval-action-stack flex flex-col gap-2">
                          {pmcDec !==
                            "APPROVED_TO_WORKFLOW" && (
                            <button
                              type="button"
                              onClick={() =>
                                approveToWorkflow(r)
                              }
                              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 shadow-sm"
                            >
                              Approve &amp; Create Workflow
                            </button>
                          )}

                          {pmcDec ===
                            "APPROVED_TO_WORKFLOW" &&
                            !r.workflowProjectId && (
                              <button
                                type="button"
                                onClick={() =>
                                  approveToWorkflow(r)
                                }
                                className="px-3 py-2 rounded-lg bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700 shadow-sm"
                              >
                                Recreate Workflow
                              </button>
                            )}

                          {pmcDec !== "REJECTED" && (
                            <button
                              type="button"
                              onClick={() =>
                                rejectProject(r)
                              }
                              className="px-3 py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold hover:bg-red-700 shadow-sm"
                            >
                              Reject Project
                            </button>
                          )}

                          {pmcDec === "REJECTED" && (
                            <button
                              type="button"
                              onClick={() =>
                                approveToWorkflow(r)
                              }
                              className="px-3 py-2 rounded-lg bg-slate-800 text-white text-[11px] font-bold hover:bg-slate-900 shadow-sm"
                            >
                              Review &amp; Approve Again
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
